import { Plus, Trash2 } from "lucide-react";

import { useEffect, useState } from "react";

import type { AirtimeAllocation, AirtimeDistribution } from "../types";

// Strips formatting and normalizes the 254-country-code form down to the local
// 0-prefixed form, purely so two different-looking entries of the same number
// (e.g. "0712345678" and "+254712345678") are recognized as duplicates.
function normalizePhone(phone: string): string {
  const digits = (phone || "").replace(/[^\d]/g, "");
  if (digits.startsWith("254")) return "0" + digits.slice(3);
  return digits;
}

// Mirrors AirtimeService#normalizePhone on the backend exactly, so this preview
// shows precisely what gets stored and sent to the airtime API: a local 07.../011...
// number (or one already in 254... form) becomes +254..., and anything else (already
// +254-prefixed, or unrecognized) passes through unchanged.
function toDialFormat(phone: string): string {
  const clean = (phone || "").trim().replace(/[\s-]/g, "");
  if (!clean) return "";
  if (clean.startsWith("0")) return "+254" + clean.slice(1);
  if (clean.startsWith("254")) return "+" + clean;
  return clean;
}

// A stable fingerprint of what the server actually has saved, so we can tell when
// it changes (after a save + refetch) and resync the editable rows to match --
// otherwise this component's local edits would silently drift from the saved truth.
function distributionsSignature(allocation: AirtimeAllocation): string {
  return (allocation.distributions ?? [])
    .map((d) => `${d.id ?? ""}:${d.phoneNumber}:${d.amount}`)
    .join("|");
}

function buildDefaultRows(allocation: AirtimeAllocation): AirtimeDistribution[] {
  if (allocation.distributions?.length) {
    return allocation.distributions;
  }

  // Nothing split yet: seed a single row for the employee's HR-registered primary
  // phone, carrying the full allocation, so there is always a valid starting point.
  return [
    {
      phoneNumber: allocation.mobilePhone || "",
      amount: allocation.monthlyAmount,
      primaryNumber: true,
    },
  ];
}

export default function AirtimeDistributionPanel({

  allocation,

  onSave,

  saving,

}: {

  allocation: AirtimeAllocation;

  onSave: (payload: AirtimeDistribution[]) => void;

  saving?: boolean;

}) {

  const [rows, setRows] = useState<AirtimeDistribution[]>(() => buildDefaultRows(allocation));

  // Tracks which version of the server's saved distributions these rows were seeded
  // from. When a save succeeds, the query refetches and `allocation` changes -- this
  // resyncs the editable rows to that fresh truth instead of silently keeping stale
  // local state around, which is what made the summary table below look wrong even
  // right after a successful save.
  const [syncedSignature, setSyncedSignature] = useState(() => distributionsSignature(allocation));

  useEffect(() => {
    const nextSignature = distributionsSignature(allocation);
    if (nextSignature !== syncedSignature) {
      setRows(buildDefaultRows(allocation));
      setSyncedSignature(nextSignature);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocation]);

  const target = Number(allocation.monthlyAmount || 0);

  const total = rows.reduce((acc, row) => acc + Number(row.amount || 0), 0);

  const remaining = target - total;

  // Every shilling allocated must land on exactly one number -- not less, not more --
  // so we never end up with an unallocated "hanging" balance.
  const isBalanced = Math.abs(remaining) < 0.01;

  const hasInvalidAmount = rows.some((row) => Number(row.amount) < 0);

  const hasMissingPhone = rows.some((row) => !row.phoneNumber);

  const normalizedNumbers = rows.map((row) => normalizePhone(row.phoneNumber));

  const duplicateIndexes = new Set<number>();
  normalizedNumbers.forEach((num, index) => {
    if (!num) return;
    const firstIndex = normalizedNumbers.indexOf(num);
    if (firstIndex !== index) {
      duplicateIndexes.add(firstIndex);
      duplicateIndexes.add(index);
    }
  });
  const hasDuplicatePhone = duplicateIndexes.size > 0;

  const canSave = isBalanced && !hasInvalidAmount && !hasMissingPhone && !hasDuplicatePhone;

  function update(index: number, field: keyof AirtimeDistribution, value: string | number) {

    setRows((current) =>

      current.map((row, rowIndex) =>

        rowIndex === index ? { ...row, [field]: value } : row,

      ),

    );

  }

  function addRow() {

    setRows((current) => [

      ...current,

      {

        phoneNumber: "",

        amount: 0,

        primaryNumber: false,

      },

    ]);

  }

  function removeRow(index: number) {

    setRows((current) => current.filter((_, rowIndex) => rowIndex !== index));

  }

  function save() {

    if (!canSave) return;

    onSave(

      rows.map((row) => ({

        phoneNumber: row.phoneNumber,

        amount: Number(row.amount || 0),

        primaryNumber: row.phoneNumber === allocation.mobilePhone,

      })),

    );

  }

  const savedDistributions = allocation.distributions ?? [];

  const savedTotal = savedDistributions.reduce((acc, row) => acc + Number(row.amount || 0), 0);

  return (

    <div className="space-y-5">

      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">

        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">

          <div>

            <h2 className="text-xl font-black text-slate-900">

              My Airtime Distribution

            </h2>

            <div className="mt-4 grid gap-3 text-sm md:grid-cols-2">

              <Info label="Allocated amount" value={`KES ${target}`} />

              <Info label="Primary phone (from HR profile)" value={allocation.mobilePhone || "Not set"} />

            </div>

          </div>

          <button

            type="button"

            onClick={addRow}

            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"

          >

            <Plus className="h-4 w-4" />

            Add Number

          </button>

        </div>

        <div className="mt-6 space-y-4">

          {rows.map((row, index) => {

            const isPrimary = index === 0;

            const isDuplicate = duplicateIndexes.has(index);

            return (

              <div key={index}>

                <div className="grid gap-4 rounded-2xl border border-slate-200 p-4 md:grid-cols-[1fr_200px_auto] md:items-center">

                  <div>

                    <input

                      type="tel"

                      value={row.phoneNumber}

                      readOnly={isPrimary}

                      onChange={(e) => update(index, "phoneNumber", e.target.value)}

                      placeholder="e.g. 0712345678"

                      className={[

                        "w-full rounded-2xl border px-4 py-3 text-sm outline-none focus:ring-4",

                        isDuplicate

                          ? "border-red-300 focus:border-red-500 focus:ring-red-100"

                          : "border-slate-300 focus:border-emerald-500 focus:ring-emerald-100",

                        isPrimary ? "bg-slate-50 text-slate-600" : "",

                      ].join(" ")}

                    />

                  </div>

                  <input

                    type="number"

                    min={0}

                    value={row.amount}

                    onChange={(e) => update(index, "amount", Number(e.target.value))}

                    placeholder="Amount"

                    className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"

                  />

                  <button

                    type="button"

                    onClick={() => removeRow(index)}

                    disabled={isPrimary}

                    title={isPrimary ? "The primary number can't be removed" : "Remove this number"}

                    className="inline-flex items-center justify-center rounded-2xl border border-red-100 px-4 py-3 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"

                  >

                    <Trash2 className="h-4 w-4" />

                  </button>

                </div>

                {isDuplicate ? (

                  <p className="mt-1 text-xs font-semibold text-red-600">

                    This number is already used in another row above.

                  </p>

                ) : null}

              </div>

            );

          })}

        </div>

        <div className="mt-6 rounded-2xl bg-slate-50 p-4">

          <div className="grid gap-4 md:grid-cols-3">

            <Info label="Total distribution" value={`KES ${total}`} />

            <Info label="Allocated" value={`KES ${target}`} />

            <Info

              label="Status"

              value={

                isBalanced

                  ? "Fully allocated"

                  : remaining > 0

                  ? `KES ${remaining} left to allocate`

                  : `KES ${Math.abs(remaining)} over allocation`

              }

              danger={!isBalanced}

            />

          </div>

          {hasDuplicatePhone ? (

            <p className="mt-3 text-xs font-semibold text-red-600">

              Each number can only appear once. Fix the duplicate number(s) highlighted above before saving.

            </p>

          ) : null}

          <button

            type="button"

            onClick={save}

            disabled={saving || !canSave}

            className="mt-5 rounded-2xl bg-emerald-600 px-6 py-3 font-black text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"

          >

            {saving ? "Saving..." : "Save Distribution"}

          </button>

          {!canSave && !saving ? (

            <p className="mt-2 text-xs text-slate-400">

              100% of your allocation must be assigned before you can save.

            </p>

          ) : null}

        </div>

      </div>

      {savedDistributions.length > 0 ? (

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">

          <h3 className="text-sm font-black text-slate-900">Current saved distribution</h3>

          <table className="mt-4 w-full text-sm">

            <thead>

              <tr className="border-b text-left text-xs font-black uppercase tracking-wide text-slate-500">

                <th className="py-2">Phone number</th>

                <th className="py-2 text-right">Amount</th>

              </tr>

            </thead>

            <tbody className="divide-y divide-slate-100">

              {savedDistributions.map((row, index) => (

                <tr key={row.id ?? index}>

                  <td className="py-2">

                    {row.phoneNumber}

                    {row.primaryNumber ? (

                      <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">

                        Primary

                      </span>

                    ) : null}

                  </td>

                  <td className="py-2 text-right font-semibold">KES {row.amount}</td>

                </tr>

              ))}

            </tbody>

            <tfoot>

              <tr className="border-t font-black text-slate-900">

                <td className="py-2">Total</td>

                <td className="py-2 text-right">KES {savedTotal}</td>

              </tr>

            </tfoot>

          </table>

        </div>

      ) : null}

    </div>

  );

}

function Info({

  label,

  value,

  danger,

}: {

  label: string;

  value: string | number;

  danger?: boolean;

}) {

  return (

    <div>

      <div className="text-xs font-black uppercase tracking-wide text-slate-500">

        {label}

      </div>

      <div

        className={[

          "mt-1 text-sm font-black",

          danger ? "text-red-600" : "text-slate-900",

        ].join(" ")}

      >

        {value}

      </div>

    </div>

  );

}
